import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logout } from '../redux/reducers/loginSlice';
import SessionTimeoutModal from './Modals/SessionTimeoutModal';

const STORAGE_KEYS = {
  TOKEN: 'token',
  USER: 'user',
} as const;

const ROUTES = {
  LOGIN: '/login',
} as const;

interface SessionTimeoutContextType {
  showTimeoutModal: () => void;
  hideTimeoutModal: () => void;
}

interface SessionTimeoutProviderProps {
  children: React.ReactNode;
}

const SessionTimeoutContext = createContext<SessionTimeoutContextType | undefined>(undefined);

export const useSessionTimeout = (): SessionTimeoutContextType => {
  const context = useContext(SessionTimeoutContext);
  if (!context) {
    throw new Error('useSessionTimeout must be used within a SessionTimeoutProvider');
  }
  return context;
};
const SessionTimeoutProvider: React.FC<SessionTimeoutProviderProps> = ({ children }) => {
  const [showModal, setShowModal] = useState(false);
  const [modalTriggered, setModalTriggered] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const showTimeoutModal = useCallback(() => {
    if (!modalTriggered && !showModal) {
      setShowModal(true);
      setModalTriggered(true);
    }
  }, [modalTriggered, showModal]);

  const hideTimeoutModal = useCallback(() => {
    setShowModal(false);
    setModalTriggered(false);
  }, []);

  const clearSessionData = useCallback(() => {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  }, []);

  const navigateToLogin = useCallback(() => {
    window.location.hash = `#${ROUTES.LOGIN}`;
  }, []);

  const handleLogout = useCallback(() => {
    try {
      dispatch(logout({}));
      clearSessionData();
      setShowModal(false);
      setModalTriggered(false);
      navigateToLogin();
    } catch (error) {
      console.error('Error during logout:', error);
      navigateToLogin();
    }
  }, [dispatch, clearSessionData, navigateToLogin]);

  const contextValue: SessionTimeoutContextType = useMemo(() => ({
    showTimeoutModal,
    hideTimeoutModal,
  }), [showTimeoutModal, hideTimeoutModal]);

  return (
    <SessionTimeoutContext.Provider value={contextValue}>
      {children}
      <SessionTimeoutModal 
        show={showModal} 
        onClose={hideTimeoutModal}
        onLogout={handleLogout}
      />
    </SessionTimeoutContext.Provider>
  );
};

export default SessionTimeoutProvider;
